import '@fontsource-variable/overpass';
import '@fontsource-variable/overpass-mono';
import '@fontsource-variable/literata/opsz.css';
import '@fontsource-variable/literata/opsz-italic.css';
import './App.css';
import { lazy, Suspense, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import Main from './page/Main';
import { MobileNavProvider, MobileNavSheet, MobileTabBar } from './page/MobileNav';

// Every page but the home page loads its own code on first visit.
const Products = lazy(() => import(/* webpackChunkName: "Products" */ './page/Products'));
const About = lazy(() => import(/* webpackChunkName: "About" */ './page/About'));
const Team = lazy(() => import(/* webpackChunkName: "Team" */ './page/Team'));
const TeamMember = lazy(() => import(/* webpackChunkName: "TeamMember" */ './page/TeamMember'));
const Faq = lazy(() => import(/* webpackChunkName: "Faq" */ './page/Faq'));
const Press = lazy(() => import(/* webpackChunkName: "Press" */ './page/Press'));
const Labs = lazy(() => import(/* webpackChunkName: "Labs" */ './page/Labs'));
const Trust = lazy(() => import(/* webpackChunkName: "Trust" */ './page/Trust'));
const Blog = lazy(() => import(/* webpackChunkName: "Blog" */ './page/Blog'));
const BlogArticles = lazy(() => import(/* webpackChunkName: "BlogArticles" */ './page/BlogArticles'));
const BlogArticle = lazy(() => import(/* webpackChunkName: "BlogArticle" */ './page/BlogArticle'));
const BlogVideos = lazy(() => import(/* webpackChunkName: "BlogVideos" */ './page/BlogVideos'));
const BlogVideo = lazy(() => import(/* webpackChunkName: "BlogVideo" */ './page/BlogVideo'));
const Events = lazy(() => import(/* webpackChunkName: "Events" */ './page/Events'));
const EventSignup = lazy(() => import(/* webpackChunkName: "EventSignup" */ './page/EventSignup'));
const EventsAdmin = lazy(() => import(/* webpackChunkName: "EventsAdmin" */ './page/EventsAdmin'));
const Research = lazy(() => import(/* webpackChunkName: "Research" */ './page/Research'));
const ResearchPaper = lazy(() => import(/* webpackChunkName: "ResearchPaper" */ './page/ResearchPaper'));
const NotFound = lazy(() => import(/* webpackChunkName: "NotFound" */ './page/NotFound'));

// Take manual control of scroll position — the browser's own 'auto' restoration
// otherwise fights the reset below on every client-side route change.
if (typeof window !== 'undefined' && 'scrollRestoration' in window.history) {
  window.history.scrollRestoration = 'manual';
}

function RouteBootstrap() {
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const route = new URLSearchParams(location.search).get('route');
    if (!route) return;

    const safeRoute = route.startsWith('/') ? route : `/${route}`;
    if (safeRoute !== `${location.pathname}${location.search}`) {
      navigate(safeRoute, { replace: true });
    }
  }, [location.pathname, location.search, navigate]);

  // Reset scroll on every route change (React Router doesn't do this by default).
  // When the URL carries a hash (e.g. /products#vigilhold), scroll to its target
  // instead; it can appear a few frames later while the page's code loads.
  // The site sets `scroll-behavior: smooth` globally, which would otherwise turn this
  // into a visible scroll animation on every navigation — snap instantly instead.
  useEffect(() => {
    if (!location.hash) {
      window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
      return undefined;
    }
    const id = decodeURIComponent(location.hash.slice(1));
    let frame;
    let tries = 0;
    let observer;
    let release;
    // Content that loads above the target (the lab dial, for one) would push
    // it down, so keep it in place for a moment unless the reader scrolls.
    const stop = () => {
      observer?.disconnect();
      clearTimeout(release);
      ['wheel', 'touchstart', 'keydown'].forEach(e => window.removeEventListener(e, stop));
    };
    const find = () => {
      const target = document.getElementById(id);
      if (!target) {
        if (tries++ < 180) frame = requestAnimationFrame(find);
        return;
      }
      const pin = () => target.scrollIntoView({ behavior: 'instant', block: 'start' });
      pin();
      observer = new ResizeObserver(pin);
      observer.observe(document.body);
      release = setTimeout(stop, 3000);
      ['wheel', 'touchstart', 'keydown'].forEach(e => window.addEventListener(e, stop, { passive: true }));
    };
    find();
    return () => { cancelAnimationFrame(frame); stop(); };
  }, [location.pathname, location.hash]);

  return null;
}

// The static #seo-content block in public/index.html is for crawlers that
// don't run JS. It stays on screen until the first page's code has loaded,
// then goes, since it would only duplicate the page's h1.
function ShellGone() {
  useEffect(() => { document.getElementById('seo-content')?.remove(); }, []);
  return null;
}

function App() {
  return (
    <BrowserRouter>
      <RouteBootstrap />
      <MobileNavProvider>
        <Suspense fallback={null}>
          <ShellGone />
          <Routes>
            <Route path="/" element={<Main />} />
            <Route path="/products" element={<Products />} />
            <Route path="/about" element={<About />} />
            <Route path="/team" element={<Team />} />
            <Route path="/team/:slug" element={<TeamMember />} />
            <Route path="/bio" element={<Navigate to="/team/aiden-mcelroy" replace />} />
            <Route path="/faq" element={<Faq />} />
            <Route path="/press" element={<Press />} />
            <Route path="/labs" element={<Labs />} />
            <Route path="/trust" element={<Trust />} />
            <Route path="/blog" element={<Blog />} />
            <Route path="/blog/articles" element={<BlogArticles />} />
            <Route path="/blog/video" element={<BlogVideos />} />
            <Route path="/blog/video/:slug" element={<BlogVideo />} />
            <Route path="/blog/:slug" element={<BlogArticle />} />
            <Route path="/research" element={<Research />} />
            <Route path="/research/:slug" element={<ResearchPaper />} />
            <Route path="/events" element={<Events />} />
            <Route path="/events/admin" element={<EventsAdmin />} />
            <Route path="/events/:slug" element={<EventSignup />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
        <MobileNavSheet />
        <MobileTabBar />
      </MobileNavProvider>
    </BrowserRouter>
  );
}

export default App;
